// ?use_ios_ui — twin of iOS 0.7.0 DragStrip: a tile with a capitals label,
// the angle to one decimal and a tick gauge. A drag reads up/right as
// positive; on two axes it locks to whichever the finger has gone further
// along once it has travelled AXIS_LOCK_PX, then applies the travel since
// touch-down in one step. A nudge that lifts before the lock still applies.

import { useCallback, useRef, useState, type PointerEvent } from 'react'
import { IOS_UI_MONO, IOS_UI_PALETTE, PANEL_LABEL_STYLE, panelTileStyle } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'

export const AXIS_LOCK_PX = 8

type TAxis = 'horizontal' | 'vertical'

interface IosDragStripProps {
  label: string
  sensitivity: number
  onDrag: (delta: number) => void
  /** Degrees at which the tick reaches the end of its line. */
  gaugeRangeDeg: number
  currentAngleDeg: number
  compact?: boolean
  testId: string
}

export function IosDragStrip({ label, sensitivity, onDrag, gaugeRangeDeg, currentAngleDeg, compact = false, testId }: IosDragStripProps) {
  const [isDragging, setIsDragging] = useState(false)
  const start = useRef<{ x: number; y: number } | null>(null)
  const lockedAxis = useRef<TAxis | null>(null)
  const applied = useRef(0)
  const onDragRef = useRef(onDrag)
  onDragRef.current = onDrag

  const apply = useCallback(
    (dx: number, dy: number, axis: TAxis) => {
      // Up and right are positive; screen y grows downward.
      const travel = axis === 'horizontal' ? dx : -dy
      onDragRef.current((travel - applied.current) * sensitivity)
      applied.current = travel
    },
    [sensitivity],
  )

  const handleDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    start.current = { x: e.clientX, y: e.clientY }
    lockedAxis.current = null
    applied.current = 0
    setIsDragging(true)
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }, [])

  const handleMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (start.current == null) return
      const dx = e.clientX - start.current.x
      const dy = e.clientY - start.current.y
      if (lockedAxis.current == null) {
        if (Math.hypot(dx, dy) < AXIS_LOCK_PX) return
        lockedAxis.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
      }
      apply(dx, dy, lockedAxis.current)
    },
    [apply],
  )

  const handleEnd = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (start.current != null && lockedAxis.current == null) {
        const dx = e.clientX - start.current.x
        const dy = e.clientY - start.current.y
        if (dx !== 0 || dy !== 0) apply(dx, dy, Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical')
      }
      start.current = null
      lockedAxis.current = null
      applied.current = 0
      setIsDragging(false)
    },
    [apply],
  )

  const fraction = Math.min(Math.max(currentAngleDeg / gaugeRangeDeg, -1), 1)

  return (
    <div
      data-testid={testId}
      role="slider"
      aria-label={label}
      aria-valuenow={Number(currentAngleDeg.toFixed(1))}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleEnd}
      onPointerCancel={handleEnd}
      style={{
        ...panelTileStyle(isDragging),
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: compact ? 4 : 5,
        padding: '0 8px',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <span data-testid={`${testId}-label`} style={{ ...PANEL_LABEL_STYLE, color: isDragging ? IOS_UI_PALETTE.accentOnDark : IOS_UI_PALETTE.label }}>
        {label.toUpperCase()}
      </span>
      <span
        data-testid={`${testId}-value`}
        style={{ fontFamily: IOS_UI_MONO, fontSize: compact ? 15 : 17, fontWeight: 500, color: 'rgba(255,255,255,0.95)', whiteSpace: 'nowrap' }}
      >
        {`${currentAngleDeg.toFixed(1)}°`}
      </span>
      <div style={{ position: 'relative', width: '100%', height: 14 }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: 6.5, height: 1, background: IOS_UI_PALETTE.track }} />
        <div
          data-testid={`${testId}-gauge-tick`}
          data-fraction={String(fraction)}
          style={{
            position: 'absolute',
            top: 0,
            left: `calc(50% + ${fraction} * (50% - 1px) - 1px)`,
            width: 2,
            height: 14,
            borderRadius: 1,
            background: IOS_UI_PALETTE.accentOnDark,
          }}
        />
      </div>
    </div>
  )
}
